import { AfterViewInit, Component, ElementRef, inject, Input, OnDestroy, signal } from '@angular/core';
import { RouterLink } from '@angular/router';

@Component({
  selector: 'app-cta-banner',
  standalone: true,
  imports: [RouterLink],
  templateUrl: './cta-banner.component.html',
  styleUrl: './cta-banner.component.css'
})
export class CtaBannerComponent implements AfterViewInit, OnDestroy {
  private readonly elementRef: ElementRef<HTMLElement> = inject(ElementRef);

  @Input() eyebrow = '';
  @Input() title = '';
  @Input() text = '';
  @Input() primaryLabel = '';
  @Input() primaryLink = '/';
  @Input() secondaryLabel = '';
  @Input() secondaryLink = '/';
  @Input() animateTitle = false;
  @Input() animateTitleTrigger: 'immediate' | 'in-view' = 'immediate';

  protected readonly titleAnimationActive = signal(false);
  private titleObserver?: IntersectionObserver;

  private activateTitleAnimation(): void {
    if (this.titleAnimationActive()) {
      return;
    }

    setTimeout(() => {
      this.titleAnimationActive.set(true);
    });
  }

  ngAfterViewInit(): void {
    if (!this.animateTitle) {
      return;
    }

    if (this.animateTitleTrigger === 'immediate') {
      this.activateTitleAnimation();
      return;
    }

    if (typeof IntersectionObserver === 'undefined') {
      this.activateTitleAnimation();
      return;
    }

    this.titleObserver = new IntersectionObserver((entries) => {
      const hasIntersected = entries.some((entry) => entry.isIntersecting);

      if (!hasIntersected) {
        return;
      }

      this.activateTitleAnimation();
      this.disconnectTitleObserver();
    }, {
      threshold: 0.25
    });

    this.titleObserver.observe(this.elementRef.nativeElement);
  }

  ngOnDestroy(): void {
    this.disconnectTitleObserver();
  }

  private disconnectTitleObserver(): void {
    this.titleObserver?.disconnect();
    this.titleObserver = undefined;
  }
}
